<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="page-header-title">
          <h2 class="mb-1">{{ $t('admin.title') }}</h2>
          <p class="text-muted">{{ $t('admin.subtitle') }}</p>
        </div>
      </div>
    </div>



    <!-- Navigation Tabs -->
    <div class="row mb-4">
      <div class="col-12">
        <ul class="nav nav-tabs" id="adminTabs" role="tablist">
          <li class="nav-item" role="presentation">
            <button 
              class="nav-link active" 
              id="dashboard-tab" 
              data-bs-toggle="tab" 
              data-bs-target="#dashboard" 
              type="button" 
              role="tab"
            >
              <i class="ph-duotone ph-house me-2"></i>
              {{ $t('admin.dashboard') }}
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button 
              class="nav-link" 
              id="tenants-tab" 
              data-bs-toggle="tab" 
              data-bs-target="#tenants" 
              type="button" 
              role="tab"
            >
              <i class="ph-duotone ph-building-office me-2"></i>
              {{ $t('superAdmin.tenants.manage') }}
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button 
              class="nav-link" 
              id="users-tab" 
              data-bs-toggle="tab" 
              data-bs-target="#users" 
              type="button" 
              role="tab"
            >
              <i class="ph-duotone ph-users me-2"></i>
              {{ $t('superAdmin.users.manage') }}
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button 
              class="nav-link" 
              id="setup-wizard-tab" 
              data-bs-toggle="tab" 
              data-bs-target="#setup-wizard" 
              type="button" 
              role="tab"
            >
              <i class="ph-duotone ph-magic-wand me-2"></i>
              {{ $t('setupWizard.title') }}
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button 
              class="nav-link" 
              id="user-messages-tab" 
              data-bs-toggle="tab" 
              data-bs-target="#user-messages" 
              type="button" 
              role="tab"
            >
              <i class="ph-duotone ph-megaphone me-2"></i>
              {{ $t('superAdmin.userMessages.title') }}
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="tab-content" id="adminTabContent">
      <!-- Dashboard Tab -->
        <div class="tab-pane fade show active" id="dashboard" role="tabpanel">
          <!-- Top utilities: 2FA status + Refresh button -->
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="d-flex align-items-center">
              <span class="text-muted me-3">
                <i class="fas fa-shield-alt me-1"></i>
                {{ $t('auth.twoFactor.status') }}:
              </span>
              <span 
                class="badge me-2" 
                :class="currentUser?.has2FA ? 'bg-success' : 'bg-secondary'"
              >
                {{ currentUser?.has2FA ? $t('auth.twoFactor.enabled') : $t('auth.twoFactor.disabled') }}
              </span>
              <router-link 
                v-if="!currentUser?.has2FA" 
                to="/auth/2fa-setup" 
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fas fa-plus me-1"></i>
                {{ $t('auth.twoFactor.enableButton') }}
              </router-link>
            </div>
            <button class="btn btn-outline-primary" @click="loadStats" :disabled="loading">
              <i class="ph-duotone ph-arrow-clockwise me-2" :class="{ 'spin': loading }"></i>
              {{ $t('superAdmin.refreshStats') }}
            </button>
          </div>

          <div class="row">
            <!-- Statistics Cards -->
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card h-100 stats-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="avtar avtar-s bg-primary text-white rounded-circle">
                        <i class="ph-duotone ph-buildings f-20"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h6 class="mb-0 text-muted small">{{ $t('superAdmin.stats.totalTenants') }}</h6>
                      <h3 class="mb-0 text-dark">{{ loading ? '...' : stats.totalTenants }}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card h-100 stats-card">
                <div class="card-body p-0">
                  <div class="row g-0 h-100">
                    <!-- Total Users -->
                    <div class="col-6 border-end d-flex align-items-center">
                      <div class="p-3 w-100">
                        <div class="d-flex align-items-center">
                          <div class="flex-shrink-0">
                            <div class="avtar avtar-s bg-primary text-white rounded-circle">
                              <i class="ph-duotone ph-users f-20"></i>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-0 text-muted small">Total Users</h6>
                            <h3 class="mb-0 text-dark">{{ loading ? '...' : stats.totalUsers }}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Active Users -->
                    <div class="col-6 d-flex align-items-center">
                      <div class="p-3 w-100">
                        <div class="d-flex align-items-center">
                          <div class="flex-shrink-0">
                            <div class="avtar avtar-s bg-success text-white rounded-circle">
                              <i class="ph-duotone ph-broadcast f-20"></i>
                            </div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-0 text-muted small">
                              Active Now
                              <span v-if="isTracking" class="ms-1" style="font-size: 0.6rem;">
                                <i class="ph ph-circle-fill blink text-success"></i>
                              </span>
                            </h6>
                            <h3 class="mb-0 text-dark">{{ activeUsers }}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card h-100 stats-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="avtar avtar-s bg-primary text-white rounded-circle">
                        <i class="ph-duotone ph-envelope f-20"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h6 class="mb-0 text-muted small">{{ $t('superAdmin.stats.weeklyAverageEmailsSent') }}</h6>
                      <h3 class="mb-0 text-dark">{{ loading ? '...' : stats.weeklyAverageEmailsSent?.toFixed(2) }}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card h-100 stats-card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0">
                      <div class="avtar avtar-s bg-primary text-white rounded-circle">
                        <i class="ph-duotone ph-list-checks f-20"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <h6 class="mb-0 text-muted small">{{ $t('superAdmin.stats.totalLookupItems') }}</h6>
                      <h3 class="mb-0 text-dark">{{ loading ? '...' : stats.totalLookupItems }}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <!-- Charts Row -->
            <div class="col-12 mb-4">
              <!-- Chart Toggle -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 text-muted">
                  <i class="ph-duotone ph-chart-bar me-2"></i>
                  {{ $t('superAdmin.stats.chartsTitle') }}
                </h5>
                <div class="d-flex gap-2" role="group">
                  <button 
                    type="button" 
                    class="btn"
                    :class="chartView === 'general' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="chartView = 'general'"
                  >
                    <i class="ph-duotone ph-chart-pie me-1"></i>
                    {{ $t('superAdmin.stats.generalCharts') }}
                  </button>
                  <button 
                    type="button" 
                    class="btn"
                    :class="chartView === 'daily' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="chartView = 'daily'"
                  >
                    <i class="ph-duotone ph-clock-countdown me-1"></i>
                    {{ $t('superAdmin.stats.dailyCharts') }}
                  </button>
                </div>
              </div>

              <div class="row g-4">
                <!-- General Charts -->
                <template v-if="chartView === 'general'">
                <!-- Quotations Overview Chart -->
                <div class="col-lg-4 col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h5 class="mb-0">
                        <i class="ph-duotone ph-chart-pie me-2"></i>
                        {{ $t('superAdmin.stats.quotationsOverview') }}
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="chart-container">
                        <canvas ref="quotationsChart" width="400" height="200"></canvas>
                      </div>
                      <div class="chart-legend mt-3">
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-success rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.completedQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.completedQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-warning rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.pendingQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.pendingQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-danger rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.failedQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.failedQuotations || 0 }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tracking Status Chart -->
                <div class="col-lg-4 col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h5 class="mb-0">
                        <i class="ph-duotone ph-list-checks me-2"></i>
                        {{ $t('superAdmin.stats.trackingStatusCounts') }}
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="chart-container">
                        <canvas ref="trackingStatusChart" width="400" height="200"></canvas>
                      </div>
                      <div class="chart-legend mt-3" style="max-height: 200px; overflow-y: auto;">
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-info rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.newQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.newQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-primary rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.reNewQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.reNewQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-warning rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.processingQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.processingQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-danger rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.processedWithErrorQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.processedWithErrorQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-success rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.processedSuccessfullyQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.processedSuccessfullyQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-info rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.readyToSendToNozzleQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.readyToSendToNozzleQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-secondary rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.enqueuedQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.enqueuedQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-dark rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.deletedFromNozzleQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.deletedFromNozzleQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-warning rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.revisionRequestedQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.revisionRequestedQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-danger rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.declinedQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.declinedQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-danger rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.declinedSuccessfullyQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.declinedSuccessfullyQuotations || 0 }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Import Log Status Chart -->
                <div class="col-lg-4 col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h5 class="mb-0">
                        <i class="ph-duotone ph-upload me-2"></i>
                        {{ $t('superAdmin.stats.importLogStatusCounts') }}
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="chart-container">
                        <canvas ref="importLogChart" width="400" height="200"></canvas>
                      </div>
                      <div class="chart-legend mt-3">
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-warning rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.pendingImportLogs') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.pendingImportLogs || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-info rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.inProgressImportLogs') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.inProgressImportLogs || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-success rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.successImportLogs') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.successImportLogs || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-danger rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.failedImportLogs') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.failedImportLogs || 0 }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </template>

                <!-- Daily Charts (Last 24 Hours) -->
                <template v-if="chartView === 'daily'">
                <!-- Last 24 Hours Quotations Chart -->
                <div class="col-lg-4 col-md-6 ml-2">
                  <div class="card h-100">
                    <div class="card-header">
                      <h5 class="mb-0">
                        <i class="ph-duotone ph-clock-countdown me-2"></i>
                        {{ $t('superAdmin.stats.last24HoursQuotations') }}
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="chart-container">
                        <canvas ref="last24HoursChart" width="400" height="200"></canvas>
                      </div>
                      <div class="chart-legend mt-3">
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-success rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.completedQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.last24HoursCompletedQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-warning rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.pendingQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.last24HoursPendingQuotations || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-danger rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.failedQuotations') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.last24HoursFailedQuotations || 0 }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Last 24 Hours Import Logs Chart -->
                <div class="col-lg-4 col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h5 class="mb-0">
                        <i class="ph-duotone ph-clock-countdown me-2"></i>
                        {{ $t('superAdmin.stats.last24HoursImportLogs') }}
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="chart-container">
                        <canvas ref="last24HoursImportChart" width="400" height="200"></canvas>
                      </div>
                      <div class="chart-legend mt-3">
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-warning rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.pendingImportLogs') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.last24HoursPendingImportLogs || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-success rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.successImportLogs') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.last24HoursSuccessImportLogs || 0 }}</div>
                          </div>
                        </div>
                        <div class="legend-item d-flex align-items-center mb-2">
                          <div class="legend-color bg-danger rounded me-2" style="width: 12px; height: 12px;"></div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold small">{{ $t('superAdmin.stats.failedImportLogs') }}</div>
                            <div class="text-muted" style="font-size: 0.75rem;">{{ stats.last24HoursFailedImportLogs || 0 }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </template>
              </div>
            </div>

          <!-- Quotation Errors Table -->
            <div class="col-12 mb-4">
              <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">
                    <i class="ph-duotone ph-warning-circle me-2"></i>
                    {{ $t('superAdmin.quotationErrors.title') }}
                  </h5>
                  <button 
                    class="btn btn-sm btn-outline-primary" 
                    @click="loadQuotationErrors" 
                    :disabled="loadingErrors"
                  >
                    <i class="ph-duotone ph-arrow-clockwise me-1" :class="{ 'spin': loadingErrors }"></i>
                    {{ $t('common.refresh') }}
                  </button>
                </div>
                <div class="card-body">
                  <div v-if="loadingErrors" class="text-center py-3">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">{{ $t('common.loading') }}</span>
                    </div>
                  </div>
                  <div v-else-if="quotationErrors.length > 0" class="table-responsive">
                    <table class="table table-hover">
                      <caption class="visually-hidden">{{ $t('common.dataTableCaption') }}</caption>
                      <thead>
                        <tr>
                          <th>{{ $t('superAdmin.quotationErrors.columns.code') }}</th>
                          <th>{{ $t('superAdmin.quotationErrors.columns.title') }}</th>
                          <th>{{ $t('superAdmin.quotationErrors.columns.requestForQuotationCodes') }}</th>
                          <th>{{ $t('superAdmin.quotationErrors.columns.tenantName') }}</th>
                          <th>{{ $t('superAdmin.quotationErrors.columns.clientTenantName') }}</th>
                          <th>{{ $t('superAdmin.quotationErrors.columns.errorMessage') }}</th>
                          <th>{{ $t('superAdmin.quotationErrors.columns.lastErrorOccurrenceTime') }}</th>
                          <th class="text-center">{{ $t('superAdmin.quotationErrors.columns.actions') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="error in quotationErrors" :key="error.id">
                          <td>{{ error.code || '-' }}</td>
                          <td>{{ error.title || '-' }}</td>
                          <td>{{ error.requestForQuotationCodes || '-' }}</td>
                          <td>{{ error.tenantName || '-' }}</td>
                          <td>{{ error.clientTenantName || '-' }}</td>
                          <td>
                            <span class="text-danger" style="max-width: 300px; display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="error.lastErrorMessage">
                              {{ error.lastErrorMessage || '-' }}
                            </span>
                          </td>
                          <td>{{ formatDateTime(error.lastErrorOccurrenceTime) }}</td>
                          <td class="text-center">
                            <div class="btn-group" role="group">
                              <button
                                class="btn btn-sm btn-outline-primary"
                                @click="resendQuotationError(error)"
                                :disabled="resendingError === error.id"
                                :title="$t('superAdmin.quotationErrors.actions.resend')"
                              >
                                <i v-if="resendingError === error.id" class="ph-duotone ph-spinner spin"></i>
                                <i v-else class="ph-duotone ph-paper-plane-tilt"></i>
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                @click="confirmDeleteError(error)"
                                :disabled="deletingError === error.id"
                                :title="$t('superAdmin.quotationErrors.actions.delete')"
                              >
                                <i v-if="deletingError === error.id" class="ph-duotone ph-spinner spin"></i>
                                <i v-else class="ph-duotone ph-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <!-- Pagination -->
                    <div v-if="quotationErrorsPagination.totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
                      <div class="text-muted small">
                        {{ $t('common.pagination.showing').replace('{from}', (quotationErrorsPagination.currentPage - 1) * quotationErrorsPagination.pageSize + 1).replace('{to}', Math.min(quotationErrorsPagination.currentPage * quotationErrorsPagination.pageSize, quotationErrorsPagination.totalCount)).replace('{total}', quotationErrorsPagination.totalCount) }}
                      </div>
                      <nav :aria-label="$t('superAdminPagination.quotationErrors.paginationLabel')">
                        <ul class="pagination pagination-sm mb-0">
                          <li class="page-item" :class="{ disabled: quotationErrorsPagination.currentPage === 1 }">
                            <button class="page-link" @click="changeQuotationErrorsPage(quotationErrorsPagination.currentPage - 1)" :disabled="quotationErrorsPagination.currentPage === 1">
                              {{ $t('common.pagination.previous') }}
                            </button>
                          </li>
                          <li class="page-item" :class="{ active: page === quotationErrorsPagination.currentPage }" v-for="page in getVisiblePages(quotationErrorsPagination.currentPage, quotationErrorsPagination.totalPages)" :key="page">
                            <button class="page-link" @click="changeQuotationErrorsPage(page)">{{ page }}</button>
                          </li>
                          <li class="page-item" :class="{ disabled: quotationErrorsPagination.currentPage === quotationErrorsPagination.totalPages }">
                            <button class="page-link" @click="changeQuotationErrorsPage(quotationErrorsPagination.currentPage + 1)" :disabled="quotationErrorsPagination.currentPage === quotationErrorsPagination.totalPages">
                              {{ $t('common.pagination.next') }}
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                  <div v-else class="text-center text-muted py-3">
                    <i class="ph-duotone ph-warning-circle" style="font-size: 3rem;"></i>
                    <p class="mt-2">{{ $t('superAdmin.quotationErrors.noData') }}</p>
                  </div>
                </div>
              </div>
            </div>

          <!-- Import Log Errors Table -->
            <div class="col-12 mb-4">
              <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">
                    <i class="ph-duotone ph-warning-circle me-2"></i>
                    {{ $t('superAdmin.importLogErrors.title') }}
                  </h5>
                  <button 
                    class="btn btn-sm btn-outline-primary" 
                    @click="loadImportLogErrors" 
                    :disabled="loadingImportLogErrors"
                  >
                    <i class="ph-duotone ph-arrow-clockwise me-1" :class="{ 'spin': loadingImportLogErrors }"></i>
                    {{ $t('common.refresh') }}
                  </button>
                </div>
                <div class="card-body">
                  <div v-if="loadingImportLogErrors" class="text-center py-3">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">{{ $t('common.loading') }}</span>
                    </div>
                  </div>
                  <div v-else-if="importLogErrors.length > 0" class="table-responsive">
                    <table class="table table-hover">
                      <caption class="visually-hidden">{{ $t('common.dataTableCaption') }}</caption>
                      <thead>
                        <tr>
                          <th>{{ $t('superAdmin.importLogErrors.columns.tenantName') }}</th>
                          <th>{{ $t('superAdmin.importLogErrors.columns.type') }}</th>
                          <th>{{ $t('superAdmin.importLogErrors.columns.sourceReferenceId') }}</th>
                          <th>{{ $t('superAdmin.importLogErrors.columns.status') }}</th>
                          <th class="text-center">{{ $t('superAdmin.importLogErrors.columns.retryCount') }}</th>
                          <th>{{ $t('superAdmin.importLogErrors.columns.lastRetryAttempt') }}</th>
                          <th>{{ $t('superAdmin.importLogErrors.columns.creationTime') }}</th>
                          <th>{{ $t('superAdmin.importLogErrors.columns.lastModificationTime') }}</th>
                          <th>{{ $t('superAdmin.importLogErrors.columns.errorMessage') }}</th>
                          <th class="text-center">{{ $t('superAdmin.importLogErrors.columns.actions') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="error in importLogErrors" :key="error.id">
                          <td>{{ error.tenantName || '-' }}</td>
                          <td>{{ error.type || '-' }}</td>
                          <td>{{ error.sourceReferenceId || '-' }}</td>
                          <td>{{ error.status || '-' }}</td>
                          <td class="text-center">{{ error.retryCount ?? 0 }}</td>
                          <td>{{ error.lastRetryAttempt ? formatDateTime(error.lastRetryAttempt) : '-' }}</td>
                          <td>{{ error.creationTime ? formatDateTime(error.creationTime) : '-' }}</td>
                          <td>{{ error.lastModificationTime ? formatDateTime(error.lastModificationTime) : '-' }}</td>
                          <td>
                            <span
                              class="text-danger"
                              style="max-width: 420px; display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                              :title="error.errorMessage"
                            >
                              {{ error.errorMessage || '-' }}
                            </span>
                          </td>
                          <td class="text-center">
                            <div class="btn-group" role="group">
                              <button
                                class="btn btn-sm btn-outline-primary"
                                @click="retryImportLogError(error)"
                                :disabled="retryingImportLogError === error.id"
                                :title="$t('superAdmin.importLogErrors.actions.retry')"
                              >
                                <i v-if="retryingImportLogError === error.id" class="ph-duotone ph-spinner spin"></i>
                                <i v-else class="ph-duotone ph-arrow-clockwise"></i>
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                @click="confirmDeleteImportLogError(error)"
                                :disabled="deletingImportLogError === error.id"
                                :title="$t('superAdmin.importLogErrors.actions.delete')"
                              >
                                <i v-if="deletingImportLogError === error.id" class="ph-duotone ph-spinner spin"></i>
                                <i v-else class="ph-duotone ph-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <!-- Pagination -->
                    <div v-if="importLogErrorsPagination.totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
                      <div class="text-muted small">
                        {{ $t('common.pagination.showing').replace('{from}', (importLogErrorsPagination.currentPage - 1) * importLogErrorsPagination.pageSize + 1).replace('{to}', Math.min(importLogErrorsPagination.currentPage * importLogErrorsPagination.pageSize, importLogErrorsPagination.totalCount)).replace('{total}', importLogErrorsPagination.totalCount) }}
                      </div>
                      <nav :aria-label="$t('superAdminPagination.importLogErrors.paginationLabel')">
                        <ul class="pagination pagination-sm mb-0">
                          <li class="page-item" :class="{ disabled: importLogErrorsPagination.currentPage === 1 }">
                            <button class="page-link" @click="changeImportLogErrorsPage(importLogErrorsPagination.currentPage - 1)" :disabled="importLogErrorsPagination.currentPage === 1">
                              {{ $t('common.pagination.previous') }}
                            </button>
                          </li>
                          <li class="page-item" :class="{ active: page === importLogErrorsPagination.currentPage }" v-for="page in getVisiblePages(importLogErrorsPagination.currentPage, importLogErrorsPagination.totalPages)" :key="page">
                            <button class="page-link" @click="changeImportLogErrorsPage(page)">{{ page }}</button>
                          </li>
                          <li class="page-item" :class="{ disabled: importLogErrorsPagination.currentPage === importLogErrorsPagination.totalPages }">
                            <button class="page-link" @click="changeImportLogErrorsPage(importLogErrorsPagination.currentPage + 1)" :disabled="importLogErrorsPagination.currentPage === importLogErrorsPagination.totalPages">
                              {{ $t('common.pagination.next') }}
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                  <div v-else class="text-center text-muted py-3">
                    <i class="ph-duotone ph-warning-circle" style="font-size: 3rem;"></i>
                    <p class="mt-2">{{ $t('superAdmin.importLogErrors.noData') }}</p>
                  </div>
                </div>
              </div>
            </div>

          <!-- Active Users World Map -->
          <div class="col-12 mb-4">
            <div class="card">
              <div class="card-header">
                <h5 class="mb-0">
                  <i class="ph-duotone ph-globe me-2"></i>
                  Active Users by Location
                  <span v-if="isTracking" class="badge bg-success ms-2" style="font-size: 0.65rem;">
                    <i class="ph ph-circle-fill blink"></i> LIVE
                  </span>
                </h5>
              </div>
              <div class="card-body">
                <!-- World Map -->
                <div id="world-map" style="width: 100%; height: 500px; background: #f8f9fa; border-radius: 8px;"></div>
                
                <!-- Country List -->
                <div v-if="activeUsers > 0" class="row mt-4">
                  <div class="col-12">
                    <h6 class="mb-3">
                      <i class="ph-duotone ph-list me-2"></i>
                      Active Users by Country
                    </h6>
                    <div class="table-responsive">
                      <table class="table table-hover">
                        <caption class="visually-hidden">{{ $t('common.dataTableCaption') }}</caption>
                        <thead>
                          <tr>
                            <th>Country</th>
                            <th>Active Users</th>
                            <th>Users</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in countryCounts" :key="item.country">
                            <td>
                              <i class="ph-duotone ph-map-pin text-success me-2"></i>
                              <strong>{{ getCountryName(item.country) }}</strong>
                              <small v-if="item.country !== getCountryName(item.country)" class="text-muted ms-1">({{ item.country }})</small>
                            </td>
                            <td>
                              <span class="badge bg-success">{{ item.count }}</span>
                            </td>
                            <td>
                              <div class="d-flex flex-wrap gap-1">
                                <span 
                                  v-for="user in activeUsersByCountry[item.country]" 
                                  :key="user.userId"
                                  class="badge bg-light text-dark"
                                  :title="user.userName"
                                >
                                  {{ user.email }}
                                </span>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center text-muted py-4">
                  <i class="ph-duotone ph-users" style="font-size: 2rem;"></i>
                  <p class="mt-2 mb-0">No active users at the moment</p>
                </div>

                <!-- Login Statistics by Country (aggregated) -->
                <div class="row mt-4">
                  <div class="col-12 col-md-6">
                    <h6 class="mb-3">
                      <i class="ph-duotone ph-globe-hemisphere-west me-2"></i>
                      {{ $t('superAdmin.stats.loginCountryStatistics') || 'Logins by Country' }}
                    </h6>
                    <div v-if="!loginCountryStats.length" class="text-muted small">
                      {{ $t('superAdmin.stats.loginCountryStatisticsEmpty') || 'No login statistics available yet.' }}
                    </div>
                    <ul
                      v-else
                      class="list-group list-group-flush small"
                    >
                      <li
                        v-for="item in loginCountryStats"
                        :key="item.loginCountry || 'unknown'"
                        class="list-group-item d-flex justify-content-between align-items-center px-0"
                      >
                        <span>{{ item.loginCountry || 'Unknown' }}</span>
                        <span class="badge bg-light-primary text-primary">
                          {{ item.userCount }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Tenants Table -->
            <div class="col-12 mb-4">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">
                    <i class="ph-duotone ph-trophy me-2"></i>
                    {{ $t('superAdmin.stats.topTenantsByCompletedQuotations') }}
                  </h5>
                </div>
                <div class="card-body">
                  <div v-if="loading" class="text-center py-3">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">{{ $t('common.loading') }}</span>
                    </div>
                  </div>
                <div v-else-if="stats.topTenantsByCompletedQuotations.length > 0">
                  <div class="d-flex justify-content-end mb-3">
                    <div class="input-group input-group-sm top-tenant-search" style="max-width: 280px;">
                      <span class="input-group-text bg-white border-end-0">
                        <i class="ph-duotone ph-magnifying-glass"></i>
                      </span>
                      <input
                        v-model="tenantKeyword"
                        type="text"
                        class="form-control border-start-0"
                        :placeholder="$t('common.search.placeholder')"
                      />
                    </div>
                  </div>
                  <div v-if="filteredTopTenants.length > 0" class="table-responsive">
                    <table class="table table-hover">
                      <caption class="visually-hidden">{{ $t('common.dataTableCaption') }}</caption>
                      <thead>
                        <tr>
                          <th>{{ $t('superAdmin.tenants.columns.name') }}</th>
                          <th class="text-center">{{ $t('superAdmin.stats.completedQuotations') }}</th>
                          <th class="text-center">{{ $t('superAdmin.stats.pendingQuotations') }}</th>
                          <th class="text-center">{{ $t('superAdmin.stats.failedQuotations') }}</th>
                          <th class="text-center">{{ $t('superAdmin.stats.totalQuotations') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="tenant in filteredTopTenants" :key="tenant.tenantId">
                          <td>{{ tenant.tenantName }}</td>
                          <td class="text-center">
                            <span class="badge badge-neutral">{{ tenant.completedQuotationsCount }}</span>
                          </td>
                          <td class="text-center">
                            <span class="badge badge-neutral">{{ tenant.pendingQuotationsCount }}</span>
                          </td>
                          <td class="text-center">
                            <span class="badge badge-neutral">{{ tenant.failedQuotationsCount }}</span>
                          </td>
                          <td class="text-center">
                            <span class="badge badge-neutral">{{ tenant.totalQuotationsCount }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-center text-muted py-3">
                    <i class="ph-duotone ph-magnifying-glass" style="font-size: 3rem;"></i>
                    <p class="mt-2">{{ $t('common.noResults') }}</p>
                  </div>
                </div>
                <div v-else class="text-center text-muted py-3">
                    <i class="ph-duotone ph-chart-bar" style="font-size: 3rem;"></i>
                    <p class="mt-2">{{ $t('superAdmin.stats.noTenantData') }}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
      </div>

      <!-- Tenants Tab -->
      <div class="tab-pane fade" id="tenants" role="tabpanel">
        <TenantManagement />
      </div>

      <!-- Users Tab -->
      <div class="tab-pane fade" id="users" role="tabpanel">
        <UserManagement />
      </div>

      <!-- Setup Wizard Tab -->
      <div class="tab-pane fade" id="setup-wizard" role="tabpanel">
        <SetupWizard />
      </div>

      <!-- User Messages Tab -->
      <div class="tab-pane fade" id="user-messages" role="tabpanel">
        <UserMessageManagement />
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { usePresence } from '@/composables/usePresence';
import superAdminService from '@/services/superAdminService';
import TenantManagement from './super-admin/TenantManagement.vue';
import UserManagement from './super-admin/UserManagement.vue';
import SetupWizard from './super-admin/SetupWizard.vue';
import UserMessageManagement from './super-admin/UserMessageManagement.vue';
import Chart from 'chart.js/auto';
import Swal from 'sweetalert2';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import countryService from '@/services/countryService';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { formatDateTime } = useDateFormatter();

// Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const currentUser = computed(() => store.getters['auth/currentUser']);

// Real-time presence tracking
const { activeUsers, activeUsersList, isTracking, watchActiveUsers, stopWatching } = usePresence();

// Group active users by country
const activeUsersByCountry = computed(() => {
  const grouped = {};
  activeUsersList.value.forEach(user => {
    const country = user.country || 'Unknown';
    if (!grouped[country]) {
      grouped[country] = [];
    }
    grouped[country].push(user);
  });
  return grouped;
});

// Country counts for display
const countryCounts = computed(() => {
  return Object.entries(activeUsersByCountry.value)
    .map(([country, users]) => ({ country, count: users.length }))
    .sort((a, b) => b.count - a.count);
});

// World map
let worldMap = null;

// Country data (loaded from API)
const countryNames = ref({});
const countryCoordinates = ref({});

// Load country data from REST Countries API
const loadCountryData = async () => {
  try {
    const [namesMap, coordsMap] = await Promise.all([
      countryService.getCountryNamesMap(),
      countryService.getCountryCoordinatesMap()
    ]);
    
    countryNames.value = namesMap;
    countryCoordinates.value = coordsMap;
    
    console.log('Country data loaded:', Object.keys(namesMap).length, 'countries');
  } catch (error) {
    console.error('Error loading country data:', error);
  }
};

const getCountryName = (code) => {
  return countryNames.value[code] || code;
};

const initializeMap = () => {
  setTimeout(() => {
    nextTick(() => {
      const mapElement = document.getElementById('world-map');
      if (!mapElement) {
        console.error('Map container not found');
        return;
      }
      
      if (worldMap) {
        console.log('Map already initialized');
        return;
      }

      try {
        console.log('Initializing Mapbox with token:', mapboxgl.accessToken ? 'Token exists' : 'No token');
        
        worldMap = new mapboxgl.Map({
          container: 'world-map',
          style: 'mapbox://styles/mapbox/light-v11',
          center: [0, 20],
          zoom: 1.5,
          projection: 'mercator'
        });

        worldMap.on('load', () => {
          console.log('Map loaded successfully');
          updateMapMarkers();
        });

        worldMap.on('error', (e) => {
          console.error('Mapbox error:', e);
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    });
  }, 500);
};

const updateMapMarkers = () => {
  if (!worldMap) {
    console.log('Map not initialized yet');
    return;
  }

  console.log('Updating markers, country counts:', countryCounts.value);

  // Remove existing markers
  const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
  existingMarkers.forEach(marker => marker.remove());

  // Add markers for each country
  countryCounts.value.forEach(item => {
    const coords = countryCoordinates.value[item.country] || countryCoordinates.value['Local'] || [0, 0];
    const users = activeUsersByCountry.value[item.country] || [];
    
    console.log(`Adding marker for ${item.country} at`, coords, `with ${item.count} users`);
    
    // Create marker element
    const el = document.createElement('div');
    el.className = 'marker';
    el.style.backgroundColor = '#28a745';
    el.style.width = `${20 + item.count * 5}px`;
    el.style.height = `${20 + item.count * 5}px`;
    el.style.borderRadius = '50%';
    el.style.border = '3px solid white';
    el.style.cursor = 'pointer';
    el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.color = 'white';
    el.style.fontWeight = 'bold';
    el.style.fontSize = '12px';
    el.textContent = item.count;

    // Create popup
    const emailList = users.map(u => `<div>• ${u.email}</div>`).join('');
    const countryName = getCountryName(item.country);
    const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
      <div style="padding: 8px;">
        <strong>${countryName}</strong><br>
        <span style="color: #28a745; font-weight: bold;">${item.count} active user${item.count > 1 ? 's' : ''}</span>
        <hr style="margin: 8px 0;">
        ${emailList}
      </div>
    `);

    // Add marker to map
    new mapboxgl.Marker(el)
      .setLngLat(coords)
      .setPopup(popup)
      .addTo(worldMap);
  });
};

// Watch for changes in active users
watch(countryCounts, () => {
  if (worldMap) {
    updateMapMarkers();
  }
}, { deep: true });

// Dashboard stats
const stats = ref({
  totalLookupItems: 0,
  totalTenants: 0,
  totalUsers: 0,
  weeklyAverageEmailsSent: 0,
  pendingQuotations: 0,
  completedQuotations: 0,
  failedQuotations: 0,
  // Individual tracking status counts
  newQuotations: 0,
  reNewQuotations: 0,
  processingQuotations: 0,
  processedWithErrorQuotations: 0,
  processedSuccessfullyQuotations: 0,
  readyToSendToNozzleQuotations: 0,
  enqueuedQuotations: 0,
  deletedFromNozzleQuotations: 0,
  revisionRequestedQuotations: 0,
  declinedQuotations: 0,
  declinedSuccessfullyQuotations: 0,
  // ImportLog status counts
  pendingImportLogs: 0,
  inProgressImportLogs: 0,
  successImportLogs: 0,
  failedImportLogs: 0,
  // Last 24 Hours stats
  last24HoursPendingQuotations: 0,
  last24HoursCompletedQuotations: 0,
  last24HoursFailedQuotations: 0,
  last24HoursPendingImportLogs: 0,
  last24HoursSuccessImportLogs: 0,
  last24HoursFailedImportLogs: 0,
  last24HoursTotalImportLogs: 0,
  topTenantsByCompletedQuotations: []
});

// Historical login statistics by country
const loginCountryStats = ref([]);

const loading = ref(false);
const tenantKeyword = ref('');
const chartView = ref('general'); // 'general' or 'daily'
const quotationsChart = ref(null);
const trackingStatusChart = ref(null);
const importLogChart = ref(null);
const last24HoursChart = ref(null);
const last24HoursImportChart = ref(null);
let quotationsChartInstance = null;
let trackingStatusChartInstance = null;
let importLogChartInstance = null;
let last24HoursChartInstance = null;
let last24HoursImportChartInstance = null;

// Quotation Errors
const quotationErrors = ref([]);
const loadingErrors = ref(false);
const deletingError = ref(null);
const resendingError = ref(null);
const quotationErrorsPagination = ref({
  currentPage: 1,
  pageSize: 10,
  totalCount: 0,
  totalPages: 0
});

// Import Log Errors
const importLogErrors = ref([]);
const loadingImportLogErrors = ref(false);
const deletingImportLogError = ref(null);
const retryingImportLogError = ref(null);
const importLogErrorsPagination = ref({
  currentPage: 1,
  pageSize: 10,
  totalCount: 0,
  totalPages: 0
});

const filteredTopTenants = computed(() => {
  const keyword = tenantKeyword.value.trim().toLowerCase();
  const tenants = stats.value.topTenantsByCompletedQuotations || [];
  
  if (!keyword) {
    return tenants;
  }
  
  return tenants.filter((tenant) => {
    const valuesToSearch = [
      tenant.tenantName,
      tenant.completedQuotationsCount,
      tenant.pendingQuotationsCount,
      tenant.failedQuotationsCount,
      tenant.totalQuotationsCount
    ];
    
    return valuesToSearch.some(value => 
      String(value ?? '')
        .toLowerCase()
        .includes(keyword)
    );
  });
});

const createChartsForView = (view) => {
  if (view === 'daily') {
    createLast24HoursChart();
    createLast24HoursImportChart();
    return;
  }

  createQuotationsChart();
  createTrackingStatusChart();
  createImportLogChart();
};

const createChartsForCurrentView = async () => {
  // Important: canvases are conditionally rendered (v-if),
  // so we must wait for DOM to update before creating charts.
  await nextTick();
  createChartsForView(chartView.value);
};

// Load dashboard stats
const loadStats = async () => {
  loading.value = true;
  try {
    const [data, loginCountries] = await Promise.all([
      superAdminService.getDashboardStats(),
      superAdminService.getLoginCountryStatistics()
    ]);

    stats.value = data;
    loginCountryStats.value = Array.isArray(loginCountries) ? loginCountries : [];

    // Update charts after stats are loaded
    await createChartsForCurrentView();
  } catch (error) {
    console.error('Error loading dashboard stats:', error);
    store.dispatch('notify', {
      message: t('superAdmin.errors.statsLoadFailed'),
      type: 'error'
    });
  } finally {
    loading.value = false;
  }
};

// Helper: Get chart options (shared across all charts)
const getChartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
          return `${context.label}: ${context.parsed} (${percentage}%)`;
        }
      }
    }
  }
});

// Helper: Create a chart instance
const createChartInstance = (canvasRef, chartInstanceRef, config) => {
  if (!canvasRef.value) return;
  
  if (chartInstanceRef) {
    chartInstanceRef.destroy();
  }
  
  const ctx = canvasRef.value.getContext('2d');
  return new Chart(ctx, config);
};

// Helper: Create quotations overview chart
const createQuotationsChart = () => {
  const labels = [
    t('superAdmin.stats.completedQuotations'),
    t('superAdmin.stats.pendingQuotations'),
    t('superAdmin.stats.failedQuotations')
  ];
  
  const data = [
    stats.value.completedQuotations || 0,
    stats.value.pendingQuotations || 0,
    stats.value.failedQuotations || 0
  ];
  
  const backgroundColor = ['#198754', '#ffc107', '#dc3545'];
  
  quotationsChartInstance = createChartInstance(
    quotationsChart,
    quotationsChartInstance,
    {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: getChartOptions()
    }
  );
};

// Helper: Create tracking status chart
const createTrackingStatusChart = () => {
  const labels = [
    t('superAdmin.stats.newQuotations'),
    t('superAdmin.stats.reNewQuotations'),
    t('superAdmin.stats.processingQuotations'),
    t('superAdmin.stats.processedWithErrorQuotations'),
    t('superAdmin.stats.processedSuccessfullyQuotations'),
    t('superAdmin.stats.readyToSendToNozzleQuotations'),
    t('superAdmin.stats.enqueuedQuotations'),
    t('superAdmin.stats.deletedFromNozzleQuotations'),
    t('superAdmin.stats.revisionRequestedQuotations'),
    t('superAdmin.stats.declinedQuotations'),
    t('superAdmin.stats.declinedSuccessfullyQuotations')
  ];
  
  const data = [
    stats.value.newQuotations || 0,
    stats.value.reNewQuotations || 0,
    stats.value.processingQuotations || 0,
    stats.value.processedWithErrorQuotations || 0,
    stats.value.processedSuccessfullyQuotations || 0,
    stats.value.readyToSendToNozzleQuotations || 0,
    stats.value.enqueuedQuotations || 0,
    stats.value.deletedFromNozzleQuotations || 0,
    stats.value.revisionRequestedQuotations || 0,
    stats.value.declinedQuotations || 0,
    stats.value.declinedSuccessfullyQuotations || 0
  ];
  
  const backgroundColor = [
    '#0dcaf0', '#0d6efd', '#ffc107', '#dc3545', '#198754',
    '#0dcaf0', '#6c757d', '#212529', '#ffc107', '#dc3545', '#dc3545'
  ];
  
  trackingStatusChartInstance = createChartInstance(
    trackingStatusChart,
    trackingStatusChartInstance,
    {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: getChartOptions()
    }
  );
};

// Helper: Create import log status chart
const createImportLogChart = () => {
  const labels = [
    t('superAdmin.stats.pendingImportLogs'),
    t('superAdmin.stats.inProgressImportLogs'),
    t('superAdmin.stats.successImportLogs'),
    t('superAdmin.stats.failedImportLogs')
  ];
  
  const data = [
    stats.value.pendingImportLogs || 0,
    stats.value.inProgressImportLogs || 0,
    stats.value.successImportLogs || 0,
    stats.value.failedImportLogs || 0
  ];
  
  const backgroundColor = ['#ffc107', '#0dcaf0', '#198754', '#dc3545'];
  
  importLogChartInstance = createChartInstance(
    importLogChart,
    importLogChartInstance,
    {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: getChartOptions()
    }
  );
};

// Helper: Create last 24 hours quotations chart
const createLast24HoursChart = () => {
  const labels = [
    t('superAdmin.stats.completedQuotations'),
    t('superAdmin.stats.pendingQuotations'),
    t('superAdmin.stats.failedQuotations')
  ];
  
  const data = [
    stats.value.last24HoursCompletedQuotations || 0,
    stats.value.last24HoursPendingQuotations || 0,
    stats.value.last24HoursFailedQuotations || 0
  ];
  
  const backgroundColor = ['#198754', '#ffc107', '#dc3545'];
  
  last24HoursChartInstance = createChartInstance(
    last24HoursChart,
    last24HoursChartInstance,
    {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: getChartOptions()
    }
  );
};

// Helper: Create last 24 hours import logs chart
const createLast24HoursImportChart = () => {
  const labels = [
    t('superAdmin.stats.pendingImportLogs'),
    t('superAdmin.stats.successImportLogs'),
    t('superAdmin.stats.failedImportLogs')
  ];
  
  const data = [
    stats.value.last24HoursPendingImportLogs || 0,
    stats.value.last24HoursSuccessImportLogs || 0,
    stats.value.last24HoursFailedImportLogs || 0
  ];
  
  const backgroundColor = ['#ffc107', '#198754', '#dc3545'];
  
  last24HoursImportChartInstance = createChartInstance(
    last24HoursImportChart,
    last24HoursImportChartInstance,
    {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: getChartOptions()
    }
  );
};

// Re-create charts when switching view (v-if remounts canvases)
watch(chartView, async () => {
  await createChartsForCurrentView();
});

// Quotation Errors Functions
const loadQuotationErrors = async () => {
  loadingErrors.value = true;
  try {
    const response = await superAdminService.getQuotationErrors(
      quotationErrorsPagination.value.currentPage,
      quotationErrorsPagination.value.pageSize,
      'LastErrorOccurrenceTime DESC'
    );
    
    quotationErrors.value = response.items || [];
    
    // Update pagination info
    quotationErrorsPagination.value.totalCount = response.totalCount || 0;
    quotationErrorsPagination.value.totalPages = Math.ceil(quotationErrorsPagination.value.totalCount / quotationErrorsPagination.value.pageSize);
  } catch (error) {
    console.error('Error loading quotation errors:', error);
    store.dispatch('notify', {
      message: t('superAdmin.quotationErrors.errors.loadFailed'),
      type: 'error'
    });
  } finally {
    loadingErrors.value = false;
  }
};

const changeQuotationErrorsPage = (page) => {
  if (page >= 1 && page <= quotationErrorsPagination.value.totalPages) {
    quotationErrorsPagination.value.currentPage = page;
    loadQuotationErrors();
  }
};

const changeImportLogErrorsPage = (page) => {
  if (page >= 1 && page <= importLogErrorsPagination.value.totalPages) {
    importLogErrorsPagination.value.currentPage = page;
    loadImportLogErrors();
  }
};

const getVisiblePages = (currentPage, totalPages) => {
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return pages;
};

const confirmDeleteError = async (error) => {
  const result = await Swal.fire({
    title: t('superAdmin.quotationErrors.deleteTitle'),
    text: t('superAdmin.quotationErrors.deleteConfirm'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: t('common.delete'),
    cancelButtonText: t('common.cancel')
  });
  
  if (result.isConfirmed) {
    await deleteQuotationError(error);
  }
};

const confirmDeleteImportLogError = async (error) => {
  const result = await Swal.fire({
    title: t('superAdmin.importLogErrors.deleteTitle'),
    text: t('superAdmin.importLogErrors.deleteConfirm'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: t('common.delete'),
    cancelButtonText: t('common.cancel')
  });
  
  if (result.isConfirmed) {
    await deleteImportLogError(error);
  }
};

const deleteQuotationError = async (error) => {
  deletingError.value = error.id;
  try {
    await superAdminService.deleteQuotationError(error.id);
    store.dispatch('notify', {
      message: t('superAdmin.quotationErrors.success.deleted'),
      type: 'success'
    });
    loadQuotationErrors();
  } catch (err) {
    console.error('Error deleting quotation error:', err);
    store.dispatch('notify', {
      message: t('superAdmin.quotationErrors.errors.deleteFailed'),
      type: 'error'
    });
  } finally {
    deletingError.value = null;
  }
};

const resendQuotationError = async (error) => {
  resendingError.value = error.id;
  try {
    await superAdminService.resendQuotationError(error.id);
    store.dispatch('notify', {
      message: t('superAdmin.quotationErrors.success.resent'),
      type: 'success'
    });
    loadQuotationErrors();
  } catch (err) {
    console.error('Error resending quotation:', err);
    store.dispatch('notify', {
      message: t('superAdmin.quotationErrors.errors.resendFailed'),
      type: 'error'
    });
  } finally {
    resendingError.value = null;
  }
};

// Import Log Errors Functions
const loadImportLogErrors = async () => {
  loadingImportLogErrors.value = true;
  try {
    const response = await superAdminService.getImportLogErrors(
      importLogErrorsPagination.value.currentPage,
      importLogErrorsPagination.value.pageSize,
      'CreationTime DESC'
    );
    
    importLogErrors.value = response.items || [];
    
    importLogErrorsPagination.value.totalCount = response.totalCount || 0;
    importLogErrorsPagination.value.totalPages = Math.ceil(importLogErrorsPagination.value.totalCount / importLogErrorsPagination.value.pageSize);
  } catch (error) {
    console.error('Error loading import log errors:', error);
    store.dispatch('notify', {
      message: t('superAdmin.importLogErrors.errors.loadFailed'),
      type: 'error'
    });
  } finally {
    loadingImportLogErrors.value = false;
  }
};

const deleteImportLogError = async (error) => {
  deletingImportLogError.value = error.id;
  try {
    await superAdminService.deleteImportLogError(error.id);
    store.dispatch('notify', {
      message: t('superAdmin.importLogErrors.success.deleted'),
      type: 'success'
    });
    loadImportLogErrors();
  } catch (err) {
    console.error('Error deleting import log error:', err);
    store.dispatch('notify', {
      message: t('superAdmin.importLogErrors.errors.deleteFailed'),
      type: 'error'
    });
  } finally {
    deletingImportLogError.value = null;
  }
};

const retryImportLogError = async (error) => {
  retryingImportLogError.value = error.id;
  try {
    await superAdminService.retryImportLogError(error.id);
    store.dispatch('notify', {
      message: t('superAdmin.importLogErrors.success.retried'),
      type: 'success'
    });
    loadImportLogErrors();
  } catch (err) {
    console.error('Error retrying import log error:', err);
    store.dispatch('notify', {
      message: t('superAdmin.importLogErrors.errors.retryFailed'),
      type: 'error'
    });
  } finally {
    retryingImportLogError.value = null;
  }
};

onMounted(() => {
  // Check if user is super admin
  if (!currentUser.value?.isSuperAdmin) {
    store.dispatch('notify', {
      message: t('common.notification.unauthorized'),
      type: 'error'  
    });
    router.push('/admin');
  } else {
    // Load dashboard stats for super admin
    loadStats();
    // Load quotation errors
    loadQuotationErrors();
    // Load import log errors
    loadImportLogErrors();
    // Load country data
    loadCountryData();
    // Start watching active users
    watchActiveUsers();
    // Initialize world map
    initializeMap();
  }
});

onUnmounted(() => {
  stopWatching();
  if (worldMap) {
    worldMap.remove();
    worldMap = null;
  }
});

const disable2FA = async () => {
  if (!confirm(t('auth.twoFactor.disableButton'))) return;
  
  isLoading.value = true;
  error.value = null;
  success.value = null;
  
  try {
    await authService.disable2FA();
    
    // Update user in store
    const updatedUser = { ...currentUser.value, has2FA: false };
    store.commit('auth/SET_USER', updatedUser);
    
    success.value = t('auth.twoFactor.disableSuccess');
    
    store.dispatch('notify', {
      message: t('common.notification.twoFactorDisabled'),
      type: 'success'
    });
  } catch (err) {
    console.error('Disable 2FA error:', err);
    error.value = err.response?.data?.error?.message || 
                  err.response?.data?.message || 
                  t('auth.twoFactor.disableError');
    
    store.dispatch('notify', {
      message: t('common.notification.twoFactorDisableError'),
      type: 'error'
    });
  } finally {
    isLoading.value = false;
  }
};



</script>

<style scoped>
.page-header-title {
  margin-bottom: 2rem;
}

.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
  border-radius: 12px 12px 0 0 !important;
  border: none;
}

.card-title {
  color: #495057;
  font-weight: 600;
  margin: 0;
}

.badge {
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
}

.badge-neutral {
  background-color: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
  min-width: 48px;
  display: inline-flex;
  justify-content: center;
  font-weight: 600;
}

.list-group-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.list-group-item:last-child {
  margin-bottom: 0;
}

.btn {
  border-radius: 8px;
  font-weight: 500;
}

.form-control {
  border-radius: 8px;
  border: 1px solid #e1e5e9;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.top-tenant-search .input-group-text {
  border-radius: 20px 0 0 20px;
}

.top-tenant-search .form-control {
  border-radius: 0 20px 20px 0;
}

.alert {
  border-radius: 8px;
  border: none;
}

/* Spin animation for refresh button */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Phoenixcoded Style Stats Cards */
.stats-card {
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.avtar {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avtar-s {
  width: 48px;
  height: 48px;
}

.avtar-xs {
  width: 36px;
  height: 36px;
}

.f-20 {
  font-size: 20px;
}

.f-16 {
  font-size: 16px;
}

.stats-card h3 {
  font-weight: 600;
  font-size: 1.75rem;
}

.stats-card h6 {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Chart Styles */
.chart-container {
  position: relative;
  height: 250px;
  margin-bottom: 1rem;
}

canvas {
  max-height: 250px;
}

.chart-legend .legend-item {
  padding: 0.5rem 0;
}

.legend-color {
  min-width: 12px;
  min-height: 12px;
}

.blink {
  animation: blink 2s ease-in-out infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
</style> 